import React, { useEffect, useRef, useState } from 'react';

/**
 * Cloudflare Turnstile Component
 * 
 * Note: You may see console warnings and network errors from Cloudflare's challenge platform:
 * - "Blocked script execution in 'about:blank' because the document's frame is sandboxed" 
 *   warnings are harmless and occur when Cloudflare creates sandboxed iframes for security.
 * - 400 Bad Request or 401 Unauthorized errors from challenges.cloudflare.com/cdn-cgi/challenge-platform 
 *   typically occur if your Turnstile site key is configured only for your production domain but you're 
 *   testing on localhost. To fix this, add "localhost" to your Turnstile site's allowed domains in the
 *   Cloudflare dashboard, or create a separate test site key for development.
 * 
 * @param {string} siteKey - Cloudflare Turnstile site key (from environment variable)
 * @param {function} onVerify - Callback when verification succeeds (receives token)
 * @param {function} onError - Callback when verification fails
 * @param {function} onExpire - Callback when token expires
 * @param {string} theme - Theme: 'light' or 'dark' (default: 'light')
 * @param {string} size - Size: 'normal' or 'compact' (default: 'normal')
 * @param {string} language - Language code (default: 'de')
 * @param {boolean} resetOnError - Reset widget on error (default: true)
 */
const Turnstile = ({
  siteKey,
  onVerify,
  onError,
  onExpire,
  theme = 'light',
  size = 'normal',
  language = 'de',
  resetOnError = true
}) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [hasFailed, setHasFailed] = useState(false);
  
  // Store callbacks in refs to prevent re-renders when they change
  const onVerifyRef = useRef(onVerify);
  const onErrorRef = useRef(onError);
  const onExpireRef = useRef(onExpire);
  
  // Update refs when callbacks change
  useEffect(() => {
    onVerifyRef.current = onVerify;
    onErrorRef.current = onError;
    onExpireRef.current = onExpire;
  }, [onVerify, onError, onExpire]);
  
  // Check if we're on localhost
  const isLocalhost = typeof window !== 'undefined' && 
    (window.location.hostname === 'localhost' || 
     window.location.hostname === '127.0.0.1' ||
     window.location.hostname.startsWith('192.168.') ||
     window.location.hostname.startsWith('10.'));


  useEffect(() => {
    // Wait for Turnstile script to load
    const checkTurnstile = () => {
      if (window.turnstile) {
        setIsLoaded(true);
        return true;
      }
      return false;
    };

    // Check if already loaded
    if (checkTurnstile()) {
      return;
    }

    // Poll for script to load
    const interval = setInterval(() => {
      if (checkTurnstile()) {
        clearInterval(interval);
      }
    }, 100);

    // Timeout after 10 seconds
    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (!window.turnstile) {
        setError('Turnstile script failed to load');
        console.error('Cloudflare Turnstile script not loaded');
      }
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  useEffect(() => {
    if (!isLoaded || !containerRef.current || !siteKey || hasFailed) {
      return;
    }

    // Render Turnstile widget
    try {
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: siteKey,
        theme: theme,
        size: size,
        language: language,
        callback: (token) => {
          if (onVerifyRef.current) {
            onVerifyRef.current(token);
          }
        },
        'error-callback': (error) => {
          setHasFailed(true);
          // Provide more helpful error message for localhost domain issues
          if (isLocalhost) {
            setError('Turnstile-Verifizierung fehlgeschlagen. Wenn Sie auf localhost testen, stellen Sie sicher, dass "localhost" in den erlaubten Domains Ihrer Turnstile-Site-Key-Konfiguration in Cloudflare hinzugefügt wurde.');
            console.warn('Turnstile Error on localhost:', error);
            console.warn('To fix: Add "localhost" to your Turnstile site\'s allowed domains in Cloudflare dashboard, or use Cloudflare\'s test keys for development.');
            // Don't reset on localhost errors to prevent retry loop
            if (widgetIdRef.current && window.turnstile) {
              try {
                window.turnstile.remove(widgetIdRef.current);
                widgetIdRef.current = null;
              } catch (err) {
                // Ignore removal errors
              }
            }
          } else {
            setError('Verification failed');
            if (resetOnError && widgetIdRef.current) {
              window.turnstile.reset(widgetIdRef.current);
            }
          }
          if (onErrorRef.current) {
            onErrorRef.current(error);
          }
        },
        'expired-callback': () => {
          if (onExpireRef.current) {
            onExpireRef.current();
          }
        }
      });
    } catch (err) {
      console.error('Error rendering Turnstile:', err);
      setError('Failed to initialize Turnstile');
    }

    // Cleanup function
    return () => {
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (err) {
          console.error('Error removing Turnstile widget:', err);
        }
      }
    };
  }, [isLoaded, siteKey, theme, size, language, resetOnError, hasFailed, isLocalhost]);


  if (!siteKey) {
    console.warn('Turnstile: Site key is missing. Please set VITE_TURNSTILE_SITE_KEY environment variable.');
    return (
      <div className="text-sm text-text-secondary p-4 bg-muted rounded-lg">
        Turnstile configuration missing
      </div>
    );
  }

  if (error && !isLoaded) {
    return (
      <div className="text-sm text-destructive p-4 bg-red-50 rounded-lg border border-red-200">
        {error}
      </div>
    );
  }

  // Show helpful message on localhost if Turnstile failed
  if (hasFailed && isLocalhost) {
    return (
      <div className="turnstile-container">
        <div className="text-sm text-amber-600 dark:text-amber-400 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
          <p className="font-semibold mb-2">⚠️ Turnstile nicht konfiguriert für localhost</p>
          <p className="mb-2">Um Turnstile auf localhost zu verwenden:</p>
          <ol className="list-decimal list-inside space-y-1 text-xs">
            <li>Gehen Sie zu <a href="https://dash.cloudflare.com/?to=/:account/turnstile" target="_blank" rel="noopener noreferrer" className="underline">Cloudflare Dashboard → Turnstile</a></li>
            <li>Bearbeiten Sie Ihre Turnstile-Site-Einstellungen</li>
            <li>Fügen Sie <code className="bg-amber-100 dark:bg-amber-900/40 px-1 rounded">localhost</code> zu den erlaubten Domains hinzu</li>
            <li>Oder verwenden Sie Cloudflare's Test-Keys für die Entwicklung</li>
          </ol>
          <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
            <strong>Hinweis:</strong> Das Formular funktioniert weiterhin, aber ohne Bot-Schutz.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="turnstile-container">
      <div ref={containerRef} className="flex justify-center" />
      {error && (
        <p className="text-sm text-destructive mt-2">{error}</p>
      )}
    </div>
  );
};

export default Turnstile;

